/**
 * Minimal MPEG-TS timestamp rewriter.
 *
 * Concatenating HLS segments can produce a transport stream whose PTS/DTS/PCR jump
 * mid-file, because a segment carries the timestamps of its own position in the live
 * stream and the 33-bit clock wraps roughly every 26.5 hours. Players and probes then
 * report a nonsense duration (a 20 second file can read as 25 hours).
 *
 * This walks the 188-byte packets, splits the stream into runs at those jumps, and shifts
 * each run so the whole file starts at zero and stays monotonic — the same correction
 * `ffmpeg -fflags +genpts` performs, without ffmpeg.
 *
 * The core is streaming so it can sit in the download write path; `remuxTsTimestamps`
 * wraps it for one-shot use on a complete buffer.
 */

export const TS_PACKET_SIZE = 188;
export const TS_SYNC_BYTE = 0x47;

const TS_CLOCK_HZ = 90000;
const TIMESTAMP_MODULO = 2 ** 33;
// A jump larger than this between consecutive packets means a new run, not playback.
const DISCONTINUITY_THRESHOLD_TICKS = 5 * TS_CLOCK_HZ;
// Nominal spacing inserted between two runs that were not adjacent in the source.
const SEGMENT_GAP_TICKS = 1500;

type TimestampField = {
    /** Absolute offset of the 5-byte timestamp inside the buffer. */
    offset: number;
    /** 0x2/0x3 for PTS, 0x1 for DTS — the 4-bit prefix that must be preserved. */
    prefix: number;
    value: number;
};

type UnwrappedTimestampField = TimestampField & {
    unwrappedValue: number;
};

export type TsRemuxSegment = {
    startSeconds: number;
    endSeconds: number;
    durationSeconds: number;
    sourceStartSeconds: number;
};

export type TsRemuxStats = {
    packets: number;
    rewrittenTimestamps: number;
    rewrittenPcr: number;
    rewrittenContinuityCounters: number;
    durationSeconds: number;
    /** True when the input stopped looking like aligned MPEG-TS and was passed through. */
    passthrough: boolean;
    segments: TsRemuxSegment[];
};

export function readTimestamp(data: Uint8Array, offset: number) {
    const high = (data[offset] & 0x0e) >> 1;
    const middle = ((data[offset + 1] << 8) | data[offset + 2]) >>> 1;
    const low = ((data[offset + 3] << 8) | data[offset + 4]) >>> 1;

    return high * 0x40000000 + middle * 0x8000 + low;
}

export function writeTimestamp(data: Uint8Array, offset: number, prefix: number, value: number) {
    const normalized = ((value % TIMESTAMP_MODULO) + TIMESTAMP_MODULO) % TIMESTAMP_MODULO;
    const high = Math.floor(normalized / 0x40000000) & 0x07;
    const middle = Math.floor(normalized / 0x8000) & 0x7fff;
    const low = normalized & 0x7fff;

    data[offset] = (prefix << 4) | (high << 1) | 0x01;
    data[offset + 1] = (middle >> 7) & 0xff;
    data[offset + 2] = ((middle & 0x7f) << 1) | 0x01;
    data[offset + 3] = (low >> 7) & 0xff;
    data[offset + 4] = ((low & 0x7f) << 1) | 0x01;
}

export function readPcrBase(data: Uint8Array, offset: number) {
    return (
        data[offset] * 0x2000000 +
        data[offset + 1] * 0x20000 +
        data[offset + 2] * 0x200 +
        data[offset + 3] * 0x2 +
        ((data[offset + 4] & 0x80) >> 7)
    );
}

function writePcrBase(data: Uint8Array, offset: number, base: number, extension: number) {
    const normalized = ((base % TIMESTAMP_MODULO) + TIMESTAMP_MODULO) % TIMESTAMP_MODULO;

    data[offset] = Math.floor(normalized / 0x2000000) & 0xff;
    data[offset + 1] = Math.floor(normalized / 0x20000) & 0xff;
    data[offset + 2] = Math.floor(normalized / 0x200) & 0xff;
    data[offset + 3] = Math.floor(normalized / 0x2) & 0xff;
    data[offset + 4] = ((normalized & 0x01) << 7) | 0x7e | ((extension >> 8) & 0x01);
    data[offset + 5] = extension & 0xff;
}

/** PES stream ids that carry no optional header, so have no PTS/DTS to patch. */
function hasPesHeader(streamId: number) {
    return (
        streamId !== 0xbc && // program_stream_map
        streamId !== 0xbe && // padding_stream
        streamId !== 0xbf && // private_stream_2
        streamId !== 0xf0 && // ECM
        streamId !== 0xf1 && // EMM
        streamId !== 0xf2 && // DSMCC
        streamId !== 0xf8 && // ITU-T H.222.1 type E
        streamId !== 0xff // program_stream_directory
    );
}

function inspectPacket(data: Uint8Array, packetStart: number) {
    const pid = ((data[packetStart + 1] & 0x1f) << 8) | data[packetStart + 2];
    const adaptationFieldControl = (data[packetStart + 3] & 0x30) >> 4;
    const payloadUnitStart = (data[packetStart + 1] & 0x40) !== 0;
    const hasAdaptationField = adaptationFieldControl === 2 || adaptationFieldControl === 3;
    const hasPayload = adaptationFieldControl === 1 || adaptationFieldControl === 3;
    const packetEnd = packetStart + TS_PACKET_SIZE;

    let payloadStart = packetStart + 4;
    let pcrOffset = -1;

    if (hasAdaptationField) {
        const adaptationFieldLength = data[packetStart + 4];
        payloadStart = packetStart + 5 + adaptationFieldLength;

        if (adaptationFieldLength > 0 && (data[packetStart + 5] & 0x10) !== 0) {
            pcrOffset = packetStart + 6;
        }
    }

    const timestamps: TimestampField[] = [];

    if (hasPayload && payloadUnitStart && payloadStart + 9 <= packetEnd) {
        const isPesStart =
            data[payloadStart] === 0x00 &&
            data[payloadStart + 1] === 0x00 &&
            data[payloadStart + 2] === 0x01;

        if (isPesStart && hasPesHeader(data[payloadStart + 3])) {
            const ptsDtsFlags = (data[payloadStart + 7] & 0xc0) >> 6;
            const ptsOffset = payloadStart + 9;

            if (ptsDtsFlags === 2 && ptsOffset + 5 <= packetEnd) {
                timestamps.push({
                    offset: ptsOffset,
                    prefix: 0x2,
                    value: readTimestamp(data, ptsOffset),
                });
            } else if (ptsDtsFlags === 3 && ptsOffset + 10 <= packetEnd) {
                timestamps.push({
                    offset: ptsOffset,
                    prefix: 0x3,
                    value: readTimestamp(data, ptsOffset),
                });
                timestamps.push({
                    offset: ptsOffset + 5,
                    prefix: 0x1,
                    value: readTimestamp(data, ptsOffset + 5),
                });
            }
        }
    }

    return {pid, hasPayload, pcrOffset, timestamps};
}

export type TsRemuxer = {
    /** Rewrites a chunk, buffering any trailing partial packet until the next call. */
    transform(chunk: Uint8Array): Uint8Array;
    /** Returns any buffered bytes that never completed a packet. */
    flush(): Uint8Array;
    readonly stats: TsRemuxStats;
};

export function createTsRemuxer(): TsRemuxer {
    let leftover = new Uint8Array(0);
    let offset = 0;
    let offsetInitialized = false;
    let latestInput: number | null = null;
    let lastOutput = 0;
    let passthrough = false;

    const continuityCounters = new Map<number, number>();
    const stats: TsRemuxStats = {
        packets: 0,
        rewrittenTimestamps: 0,
        rewrittenPcr: 0,
        rewrittenContinuityCounters: 0,
        durationSeconds: 0,
        passthrough: false,
        segments: [],
    };

    function startSegment(sourceStart: number) {
        offset = (offsetInitialized ? lastOutput + SEGMENT_GAP_TICKS : 0) - sourceStart;
        offsetInitialized = true;
        stats.segments.push({
            sourceStartSeconds: sourceStart / TS_CLOCK_HZ,
            startSeconds: (sourceStart + offset) / TS_CLOCK_HZ,
            endSeconds: (sourceStart + offset) / TS_CLOCK_HZ,
            durationSeconds: 0,
        });
    }

    function noteOutput(value: number) {
        if (value <= lastOutput) return;
        lastOutput = value;

        const segment = stats.segments[stats.segments.length - 1];
        if (segment) {
            segment.endSeconds = value / TS_CLOCK_HZ;
            segment.durationSeconds = segment.endSeconds - segment.startSeconds;
        }
        stats.durationSeconds = lastOutput / TS_CLOCK_HZ;
    }

    function unwrapTimestamp(value: number, nearestTo: number) {
        return value + Math.round((nearestTo - value) / TIMESTAMP_MODULO) * TIMESTAMP_MODULO;
    }

    function initializeOffset(data: Uint8Array, packetCount: number) {
        let anchor: number | null = null;
        let earliest: number | null = null;

        initialPackets: for (let index = 0; index < packetCount; index++) {
            const packet = inspectPacket(data, index * TS_PACKET_SIZE);
            const values = packet.timestamps.map(timestamp => timestamp.value);
            if (packet.pcrOffset !== -1) {
                values.push(readPcrBase(data, packet.pcrOffset));
            }

            for (const value of values) {
                const unwrapped = anchor === null ? value : unwrapTimestamp(value, anchor);
                if (anchor !== null && Math.abs(unwrapped - anchor) > DISCONTINUITY_THRESHOLD_TICKS) {
                    break initialPackets;
                }
                anchor ??= unwrapped;
                earliest = earliest === null ? unwrapped : Math.min(earliest, unwrapped);
            }
        }

        if (earliest !== null) {
            startSegment(earliest);
            latestInput = earliest;
        }
    }

    function processPacket(data: Uint8Array, packetStart: number) {
        const {pid, hasPayload, pcrOffset, timestamps} = inspectPacket(data, packetStart);
        let unwrappedTimestamps: UnwrappedTimestampField[] = [];

        // DTS is the decode clock when present. Unwrap it around the latest stream
        // clock so every PID stays in the same epoch across the 33-bit rollover.
        if (timestamps.length > 0) {
            const rawReference = timestamps[timestamps.length - 1].value;
            const reference =
                latestInput === null
                    ? rawReference
                    : unwrapTimestamp(rawReference, latestInput);

            const isDiscontinuity =
                latestInput !== null &&
                Math.abs(reference - latestInput) > DISCONTINUITY_THRESHOLD_TICKS;

            if (!offsetInitialized || isDiscontinuity) {
                startSegment(reference);
            }

            latestInput = reference;
            unwrappedTimestamps = timestamps.map(timestamp => ({
                ...timestamp,
                unwrappedValue: unwrapTimestamp(timestamp.value, reference),
            }));
        } else if (!offsetInitialized && pcrOffset !== -1) {
            startSegment(readPcrBase(data, pcrOffset));
        }

        for (const timestamp of unwrappedTimestamps) {
            const shifted = timestamp.unwrappedValue + offset;
            writeTimestamp(data, timestamp.offset, timestamp.prefix, shifted);
            noteOutput(shifted);
            stats.rewrittenTimestamps++;
        }

        if (pcrOffset !== -1) {
            const extension = ((data[pcrOffset + 4] & 0x01) << 8) | data[pcrOffset + 5];
            writePcrBase(data, pcrOffset, readPcrBase(data, pcrOffset) + offset, extension);
            stats.rewrittenPcr++;
        }

        // A packet without payload repeats the previous counter; otherwise it increments.
        const previous = continuityCounters.get(pid);
        const next = previous === undefined ? 0 : hasPayload ? (previous + 1) & 0x0f : previous;
        continuityCounters.set(pid, next);

        const continuityByte = data[packetStart + 3];
        if ((continuityByte & 0x0f) !== next) {
            data[packetStart + 3] = (continuityByte & 0xf0) | next;
            stats.rewrittenContinuityCounters++;
        }

        stats.packets++;
    }

    return {
        stats,
        transform(chunk: Uint8Array) {
            if (passthrough) return chunk;

            let working: Uint8Array;
            if (leftover.length === 0) {
                working = new Uint8Array(chunk);
            } else {
                working = new Uint8Array(leftover.length + chunk.length);
                working.set(leftover, 0);
                working.set(chunk, leftover.length);
            }

            const packetCount = Math.floor(working.length / TS_PACKET_SIZE);
            let processed = 0;

            if (!offsetInitialized) {
                for (let index = 0; index < packetCount; index++) {
                    if (working[index * TS_PACKET_SIZE] !== TS_SYNC_BYTE) {
                        passthrough = true;
                        stats.passthrough = true;
                        leftover = new Uint8Array(0);
                        return working;
                    }
                }
                initializeOffset(working, packetCount);
            }

            for (let index = 0; index < packetCount; index++) {
                const packetStart = index * TS_PACKET_SIZE;

                // Never risk corrupting a download: if the stream stops looking like
                // aligned MPEG-TS, hand everything back untouched from here on.
                if (working[packetStart] !== TS_SYNC_BYTE) {
                    passthrough = true;
                    stats.passthrough = true;
                    leftover = new Uint8Array(0);
                    return working;
                }

                processPacket(working, packetStart);
                processed = packetStart + TS_PACKET_SIZE;
            }

            leftover = working.slice(processed);

            return working.subarray(0, processed);
        },
        flush() {
            const remaining = leftover;
            leftover = new Uint8Array(0);
            return remaining;
        },
    };
}

/** One-shot helper: rewrites a complete transport stream held in memory. */
export function remuxTsTimestamps(input: Uint8Array): {data: Uint8Array; stats: TsRemuxStats} {
    const remuxer = createTsRemuxer();
    const transformed = remuxer.transform(input);
    const tail = remuxer.flush();

    if (tail.length === 0) {
        return {data: transformed, stats: remuxer.stats};
    }

    const data = new Uint8Array(transformed.length + tail.length);
    data.set(transformed, 0);
    data.set(tail, transformed.length);

    return {data, stats: remuxer.stats};
}
